import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getToken, setToken } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff, Fingerprint } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Check if WebAuthn / biometric is available on this device
function isBiometricAvailable() {
  return !!(window.PublicKeyCredential && navigator.credentials);
}

// Base64URL helpers
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  bytes.forEach(b => { str += String.fromCharCode(b); });
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [hasBiometric, setHasBiometric] = useState(false);
  const navigate = useNavigate();

  // Check if biometric login is available for the stored email
  useEffect(() => {
    const savedEmail = localStorage.getItem('biometric_email');
    if (savedEmail && isBiometricAvailable()) {
      setEmail(savedEmail);
      setHasBiometric(true);
    }
  }, []);

  const navigateByRole = useCallback((employee) => {
    // Honor a saved post-login redirect (e.g. from WhatsApp magic link)
    try {
      const redirectTo = sessionStorage.getItem('post_login_redirect');
      if (redirectTo) {
        sessionStorage.removeItem('post_login_redirect');
        // Use full-page replace so react-router picks up path + search cleanly
        window.location.replace(redirectTo);
        return;
      }
    } catch { /* ignore */ }
    const userRole = (employee.role_name || employee.role || '').toLowerCase().trim();
    // Route by role — partner MUST be checked before the manager/director
    // heuristic, because a role like "Managing Partner" contains "manager"
    // and would otherwise mis-route the partner to the management dashboard.
    if (userRole === 'partner' || userRole.includes('partner')) {
      navigate('/partner-portal');
    } else if (['admin', 'administrator', 'manager', 'management', 'director'].some(r => userRole.includes(r))) {
      navigate('/dashboard/management');
    } else {
      navigate('/dashboard/employee');
    }
  }, [navigate]);

  const handleBiometricLogin = async () => {
    const savedEmail = localStorage.getItem('biometric_email');
    if (!savedEmail) { toast.error('No biometric account found'); return; }
    setBiometricLoading(true);
    try {
      // 1. Get login options from server
      const optRes = await fetch(`${API}/api/webauthn/login/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: savedEmail, origin: window.location.origin }),
      });
      if (!optRes.ok) { const e = await optRes.json(); throw new Error(e.detail || 'Failed'); }
      const options = await optRes.json();

      // 2. Prompt biometric (Face ID / fingerprint)
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: base64urlToBuffer(options.challenge),
          rpId: options.rpId,
          allowCredentials: options.allowCredentials.map(c => ({
            ...c, id: base64urlToBuffer(c.id),
          })),
          userVerification: options.userVerification,
          timeout: options.timeout,
        }
      });

      // 3. Verify with server
      const verifyRes = await fetch(`${API}/api/webauthn/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: savedEmail,
          credential_id: bufferToBase64url(assertion.rawId),
          authenticator_data: bufferToBase64url(assertion.response.authenticatorData),
          client_data_json: bufferToBase64url(assertion.response.clientDataJSON),
          signature: bufferToBase64url(assertion.response.signature),
        }),
      });
      if (!verifyRes.ok) { const e = await verifyRes.json(); throw new Error(e.detail || 'Verification failed'); }
      const data = await verifyRes.json();

      // 4. Store token (sessionStorage matches the rest of the auth flow; primary auth is HTTP-only cookie)
      setToken(data.token);
      sessionStorage.setItem('user', JSON.stringify(data));
      onLogin(data);
      toast.success('Signed in with biometric');
      navigateByRole(data);
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        toast.error('Biometric cancelled or not recognized');
      } else {
        toast.error(e.message || 'Biometric login failed');
      }
    }
    setBiometricLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await api.login(email, password);
      onLogin(data.employee);
      toast.success('Login successful!');
      navigateByRole(data.employee);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      if (detail) {
        toast.error(detail);
      } else if (status) {
        toast.error(`Login failed (Error ${status})`);
      } else {
        toast.error('Login failed — unable to reach server');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Left Panel — Building Photo */}
      <div className="hidden lg:block lg:w-[55%] relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=900&fit=crop&q=80"
          alt="Skyline"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/60 via-slate-950/30 to-slate-950" />

        {/* Branding over the photo */}
        <div className="relative z-10 flex flex-col justify-between h-full p-10 xl:p-14">
          <div className="flex items-center gap-4">
            <img src="/nirman-logo.png" alt="Nirman" className="w-[125px] h-[125px] rounded object-contain brightness-0 invert" />
            <div className="leading-none">
              <span className="font-bold text-4xl text-white tracking-tight">NIRMAN</span>
              <br />
              <span className="text-lg font-semibold tracking-[.25em] text-orange-400 uppercase">Developers</span>
            </div>
          </div>

          <div className="max-w-md">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight tracking-tight">
              Building Dreams.<br />
              <span className="text-orange-400">Delivering Excellence.</span>
            </h1>
            <p className="mt-4 text-slate-300 text-sm leading-relaxed">
              Unified project intelligence for every department — from land acquisition to handover.
            </p>
          </div>

          <p className="text-slate-500 text-xs">&copy; {new Date().getFullYear()} Nirman Developers Pvt. Ltd. All rights reserved.</p>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-16 xl:px-20 bg-slate-950">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <img src="/nirman-logo.png" alt="Nirman" className="w-14 h-14 rounded object-contain brightness-0 invert" />
          <div className="leading-none">
            <span className="font-bold text-xl text-white tracking-tight">NIRMAN</span>
            <br />
            <span className="text-xs font-semibold tracking-[.25em] text-orange-400 uppercase">Developers</span>
          </div>
        </div>

        <div className="w-full max-w-[380px]">
          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white tracking-tight">Sign in</h2>
            <p className="mt-2 text-slate-400 text-sm">Access your Nirman Workhub account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-400">
                Email
              </Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-slate-900 border-slate-800 text-white placeholder:text-slate-600 focus:border-orange-500/50 focus:ring-orange-500/20 rounded-lg"
                  placeholder="you@nirmandevelopers.in"
                  data-testid="login-email-input"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-400">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 bg-slate-900 border-slate-800 text-white placeholder:text-slate-600 focus:border-orange-500/50 focus:ring-orange-500/20 rounded-lg"
                  placeholder="Enter your password"
                  data-testid="login-password-input"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold text-sm transition-colors"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          {/* Biometric Login */}
          {hasBiometric && isBiometricAvailable() && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 border-t border-slate-800/50" />
                <span className="text-xs text-slate-600">or</span>
                <div className="flex-1 border-t border-slate-800/50" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg font-medium text-sm"
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                data-testid="biometric-login-btn"
              >
                {biometricLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Fingerprint className="w-5 h-5" />
                    Sign in with Face ID / Biometric
                  </span>
                )}
              </Button>
            </div>
          )}

          {/* Subtle divider */}
          <div className="mt-8 pt-6 border-t border-slate-800/50 text-center">
            <p className="text-xs text-slate-600">
              For access requests, contact your administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
